<script setup lang="ts">
import { MetaTitle, CategoryResults, ContentListResults } from "@/types/Facet";

const route = useRoute();
const { $fireBangApiDomain, $log } = useNuxtApp();

// カテゴリ情報取得
const { data: fetchCategorydata } = await useFetch(`${$fireBangApiDomain}/api/facet/cate/id/${route.params.id}`, {
  key:'fetchCategorydata',
  transform: (data: CategoryResults) => {
    const results = data ? data.results : null;
    return results;
  },
});

const mainContentListSlice = ref();
const mainContentListLength = ref();

const { data: mainContentList } = await useFetch(`${$fireBangApiDomain}/api/facet/cate/${route.params.id}`, {
  key:'mainContentList',
  transform: (data: ContentListResults) => {
    const results = data ? data.results : null;
    return results;
  },
});

const mainContentListLimit = 10; // メインコンテンツ 1ページ内の記事最大表示数

mainContentListSlice.value = mainContentList.value?.slice(
  (Number(route.query.page || 1) - 1) * mainContentListLimit,
  (Number(route.query.page || 1) - 1) * mainContentListLimit +
    mainContentListLimit
);

mainContentListLength.value = mainContentList?.value?.length;

const page = ref("page");

if (!fetchCategorydata.value) {
  const error = createError({ statusCode: 404, statusMessage: "Page Not Found" });
  $log.error('カテゴリが存在しません',error);
  throw error;
}

// パラメータの数値が0の場合404を返す
if (Number(route.query.page) == 0) {
  const error = createError({ statusCode: 404, statusMessage: "Page Not Found" });
  $log.error('記事が存在しません',error);
  throw error;
}

// パラメータの数値がトータルページ数よりも大きい場合404を返す
if (
  Number(route.query.page) >
  (mainContentListLength.value + mainContentListLimit - 1) /
    mainContentListLimit
) {
  const error = createError({ statusCode: 404, statusMessage: "Page Not Found" });
  $log.error('パラメーターの値が異常です',error);
  throw error;
}

// queryの文字列が数字以外の場合404を返す
const routPageString = route.query.page;
const routPage = Number(route.query.page);
if (routPageString) {
  if (isNaN(routPage)) {
    const error = createError({ statusCode: 404, statusMessage: "Page Not Found" });
    $log.error('パラメーターが不正です',error);
    throw error;
  }
}

useHead({
  bodyAttrs: {
    class: `${fetchCategorydata.value.categoryCode}`,
  },
});

const metaTitle: MetaTitle = useState("metaTitle");

metaTitle.value = fetchCategorydata.value?.pageTitle;

useSeoMeta({
  title: `${metaTitle.value}`,
  keywords: `${fetchCategorydata.value?.keywords}`,
  description: `${fetchCategorydata.value?.description}`,
  ogTitle: `${metaTitle.value}`,
  ogDescription: `${fetchCategorydata.value?.description}`,
  ogUrl: `https://fire.bang.co.jp/cate/${route.params.id}`,
});

// 2ページ目以降はnoindexになるよう設定
if (routPage >= 2) {
  useSeoMeta({
    robots: "noindex, nofollow",
  });
}

</script>

<template>
  <div>
    <OrganismsBreadcrumbs />
    <div class="wrap-contents">
      <div class="main-contents">
        <h1>
          <span>{{ fetchCategorydata?.heading1 }}</span>
        </h1>
        <div class="leadText" v-html="fetchCategorydata?.lead"></div>
        <h2>
          <span>{{ fetchCategorydata?.heading2 }}</span>
        </h2>
        <ul class="cate-box">
          <li v-for="item in mainContentListSlice" :key="item.id">
            <a :href="`/cont/${item.urlWord}/`">
              <OrganismsCDNImg
                :id="item.id"
                :table-name="'image' in item ? item.image?.tableName : ''"
                :file-name="
                  'image' in item ? '200x133_' + item.image?.fileName : ''
                "
                width="200"
                height="133"
                alt=""
              />
              <div class="box-right">
                <div class="cate-box-title">
                  {{ item.headline }}
                </div>
                <time :datetime="item.modifiedAt.toString()">{{
                  $dayjs(item.modifiedAt).format("YYYY/MM/DD")
                }}</time>
                <div class="box-text" v-html="item.lead"></div>
              </div>
            </a>
          </li>
        </ul>
        <MoleculePagination
          :total-page="
            parseInt(
              (
                (mainContentListLength + mainContentListLimit - 1) /
                mainContentListLimit
              ).toString()
            )
          "
          :query="page"
          :now="Number($route.query.page || 1)"
        />
      </div>
      <OrganismsSideContents :sideCateDisplay="true" :contTitle="`おすすめの記事`" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.breadcrumbs {
  padding: 0.8em 4% 1em;
}

/*---------------------------------------
.wrap-contents
----------------------------------------*/

.wrap-contents {
  padding: 0 0 30%;
  position: relative;
}

.pageTop {
  @include pageTop(sp);
}

/*---------------------------------------
.main-contents
----------------------------------------*/

.main-contents h1 {
  margin-bottom: 4.3%;
  padding: 1em 4%;
  font-size: px(19.9);
  font-weight: 700;
  background: url(~/assets/images/bg_pattern_02.gif) no-repeat 0 0;
  background-size: 100% 100%;
}

.main-contents h2 {
  margin-bottom: 4.3%;
  padding: 2.4% 4% 2.4% 4%;
  border-top: 2px solid $colorContentsBorder;
  border-bottom: 1px solid #fff;
  font-size: px(18);
  font-weight: 700;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(244, 244, 244, 1) 100%
  );
  background-size: 100% 100%;
  box-shadow: 0px 1px 0px 0px #dcdcdc;
}

.main-contents .leadText {
  margin-bottom: 10.7%;
  padding: 0 4%;

  :deep(p) {
    margin-bottom: 3%;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

/*---------------------------------------
.side-contents
----------------------------------------*/

.side-contents h2 {
  margin: 0 4%;
  padding: 0.5em;
  color: $colorBlue;
  background-color: $colorGray;
  text-align: center;
  font-weight: bold;
}

.side-cate,
.side-cont-recommend {
  margin: 0 0 8%;
}

.side-request {
  background-color: #f0faff;
}

.side-request-catch {
  padding: 0.5em 0;
  color: #fff;
  font-size: px(19.4);
  background-color: $colorBasicBackground;
  text-align: center;

  b {
    color: #fff800;
  }

  br {
    display: none;
  }
}

.side-request-button {
  padding: 0 4% 7.2%;

  p {
    padding: 1em 0;
    font-size: px(12.9);
  }

  .button-step1 {
    margin-bottom: 0;
  }
}

.button-step1 {
  margin: 0 auto 14%;
  padding: 4.4% 0;
  border-radius: 6px;
  display: block;
  width: 92%;
  color: #fff;
  font-weight: bold;
  background-color: $colorBasicButton;
  box-shadow: 0px 2px 0px 0px rgba(0, 0, 0, 0.16);
  text-decoration: none;
  text-align: center;
  box-sizing: border-box;
  transition: 0.1s all;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0.3);
}

.mainVisual-request .button-step1 {
  margin-bottom: 2.7%;
  width: 100%;
}

.side-request-button .button-step1 {
  margin-bottom: 0;
}

.button-step1 > {
  span {
    margin: 0 0.2em 0 0;
    padding: 0.2em 0.2em 0.3em;
    display: inline-block;
    color: #f00;
    font-size: px(17);
    font-weight: 700;
    background-color: #ff0;
    line-height: 1;
    vertical-align: middle;
  }

  div {
    padding: 0 8% 0 0;
    display: inline-block;
    color: #fff;
    font-size: px(21);
    font-weight: 700;
    background: url(~/assets/images/arrow_orange_sp.png) no-repeat right 2%
      center;
    background-size: 9% auto;
    vertical-align: middle;
    text-shadow: 2px 2px 0px rgba(241, 90, 36, 0.4);
  }
}

/*---------------------------------------
.cate-box
----------------------------------------*/

.cate-box {
  margin-bottom: 15%;
  padding: 0 4%;

  li {
    &:not(:last-of-type) {
      margin: 0 0 4.8%;
    }

    a {
      display: flex;
      align-items: flex-start;
    }

    &:not(:last-of-type) a {
      padding-bottom: 4.3%;
      border-bottom: 1px solid $colorGray;
    }

    a img {
      margin-right: 2.3%;
      width: 37%;
    }
  }
}

.cate-box-title {
  color: $colorDarkBlue;
  font-weight: 700;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cate-box li a {
  time {
    color: #999;
    font-size: px(13);
  }

  p {
    display: none;
  }
}

.box-text {
  display: none;
}


@include media(pc) {
  .breadcrumbs {
    padding: 11px 0 34px;
  }

  /*---------------------------------------
  .wrap-contents
  ----------------------------------------*/

  .wrap-contents {
    margin: 0 auto;
    padding: 6px 0 150px;
    width: $pcWrapWidth;
    display: flex;
    justify-content: space-between;
  }

  .pageTop {
    @include pageTop(pc);
  }

  /*---------------------------------------
  .main-contents
  ----------------------------------------*/

  .main-contents {
    padding: 0;
    width: $pcMainContentsWidth;

    h1 {
      margin-bottom: 30px;
      padding: 18px 18px 20px;
      font-size: px(31.9);
    }

    h2 {
      margin: 0 0 25px;
      padding: 10px 18px 10px;
      border-top: 3px solid $colorContentsBorder;
      border-bottom: 1px solid #fff;
      font-size: px(21.9);
    }

    .leadText {
      margin-bottom: 65px;
      padding: 0;

      :deep(p) {
        margin-bottom: 15px;
      }
    }
  }

  /*---------------------------------------
  .side-contents
  ----------------------------------------*/

  .side-contents {
    width: $pcSideContentsWidth;

    h2 {
      margin: 0;
      font-size: px(21.9);
    }
  }

  .side-cate,
  .side-cont-recommend {
    margin: 0 0 30px;
  }

  .side-request-catch {
    padding: 10px 0;
    font-size: px(21.9);
    font-weight: 700;

    b {
      font-weight: 700;
    }

    br {
      display: block;
    }
  }

  .side-request-button {
    padding: 0 21px 18px;

    p {
      padding: 20px 0;
      font-size: px(15);
    }

    .button-step1 {
      margin: 0 auto 3%;
      padding: 10px 0;
      width: 100%;

      > {
        span {
          margin: 0 auto;
          padding: 0.2em 0.2em 0.2em;
          width: 2.8em;
          display: block;
          font-size: px(18.9);
        }

        div {
          padding: 0;
          font-size: px(21.9);
          background: none;
        }
      }

      &:hover {
        background-color: #f78c35;
      }
    }
  }

  /*---------------------------------------
  .cate-box
  ----------------------------------------*/
  .cate-box {
    margin-bottom: 60px;
    padding: 0;

    li {
      &:not(:last-of-type) {
        margin: 0 0 25px;

        a {
          padding-bottom: 25px;
        }
      }

      a {
        &:hover {
          opacity: 0.7;
        }

        img {
          margin-right: 20px;
          width: 200px;
        }
      }
    }
  }

  .cate-box-title {
    margin-bottom: 5px;
    font-size: px(19.2);
    -webkit-line-clamp: 2;
  }

  .cate-box li a {
    time {
      margin-bottom: 5px;
      display: block;
    }

    p {
      display: block;
      color: $colorBasicText;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .box-text {
    display: block;
    color: #333333;
    color: $colorBasicText;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}
</style>
